import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import {
  Search,
  Filter,
  Plus,
  FileText,
  Clock,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  ChevronDown,
  AlertCircle,
  FileEdit,
  X,
  Cpu,
  Trash2,
  Loader2,
} from "lucide-react";
import { sopsAPI } from "../api";
import clsx from "clsx";
import { PERMISSIONS } from "../constants/roles";
import HasPermission from "../components/HasPermission";

// Standardized status configuration matching Dashboard & Machines design system
const statusConfig = {
  approved: {
    label: "Approved",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    dot: "bg-emerald-500",
    icon: CheckCircle,
  },
  pending_review: {
    label: "Pending Review",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
    icon: Clock,
  },
  draft: {
    label: "Draft",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
    icon: FileEdit,
  },
  deprecated: {
    label: "Deprecated",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
    icon: AlertCircle,
  },
};

/**
 * Renders a single SOP as a refined list row.
 * Preserves the exact 12-column listing structure:
 * Title & Category (4), Status (2), Skill (2), Duration (2), Steps (1), Action (1)
 */
const SOPListItem = ({ sop, index, onDelete }) => {
  const navigate = useNavigate();
  const status = statusConfig[sop.status] || statusConfig.draft;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }}
    >
      <div
        onClick={() => navigate(`/sops/${sop.id}`)}
        className="grid grid-cols-12 items-center px-4 py-3.5 text-sm border-b border-slate-100/80 transition-colors hover:bg-blue-50/30 group cursor-pointer"
      >
        {/* Column 1: SOP Title, Category & Machine (span 4) */}
        <div className="col-span-4 flex items-center gap-3 min-w-0 pr-4">
          <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center flex-shrink-0 group-hover:scale-105 group-hover:bg-blue-600 group-hover:text-white transition-all duration-200">
            <FileText className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors truncate text-sm">
                {sop.title}
              </span>
              {sop.is_ai_generated && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 flex-shrink-0"
                  title="AI Generated Procedure"
                >
                  <Sparkles className="w-2.5 h-2.5 text-indigo-600" />
                  AI
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 truncate">
              <span className="font-medium text-slate-600 truncate">
                {sop.category || "General"}
              </span>
              {sop.machine_name && (
                <>
                  <span className="text-slate-300">•</span>
                  <span className="text-slate-500 truncate flex items-center gap-1">
                    <Cpu className="w-3 h-3 text-slate-400" />
                    {sop.machine_name}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Column 2: Status Badge (span 2) */}
        <div className="col-span-2 flex items-center">
          <span
            className={clsx(
              "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border",
              status.badge
            )}
          >
            <span className={clsx("w-1.5 h-1.5 rounded-full", status.dot)} />
            <span>{status.label}</span>
          </span>
        </div>

        {/* Column 3: Skill Level (span 2) */}
        <div className="col-span-2 flex items-center">
          <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium text-slate-700 bg-slate-100/90 capitalize border border-slate-200/50">
            {sop.skill_level ? sop.skill_level.replace(/_/g, " ") : "All Levels"}
          </span>
        </div>

        {/* Column 4: Duration (span 2) */}
        <div className="col-span-2 flex items-center gap-1.5 text-xs font-medium text-slate-600">
          <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span>
            {sop.estimated_duration ? `${sop.estimated_duration} mins` : "Flexible"}
          </span>
        </div>

        {/* Column 5: Steps Count (span 2) */}
        <div className="col-span-2 flex items-center">
          <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-semibold text-slate-700 bg-slate-100 rounded-full">
            {Array.isArray(sop.steps) ? `${sop.steps.length} steps` : "0 steps"}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const SOPs = () => {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(1);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState(false);
  const statusFilterRef = useRef(null);
  const categoryFilterRef = useRef(null);

  const machineId = searchParams.get("machineId");
  const PAGE_SIZE = 10;
  const queryClient = useQueryClient();
  const [deletingSop, setDeletingSop] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: (id) => sopsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sops"] });
      toast.success("SOP deleted successfully");
      setDeletingSop(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Failed to delete SOP");
    },
  });

  const handleDeleteConfirm = () => {
    if (deletingSop) {
      deleteMutation.mutate(deletingSop.id);
    }
  };

  // Main SOPs query with pagination and filters
  const { data: sopsData, isLoading } = useQuery({
    queryKey: [
      "sops",
      { search, status: statusFilter, category: categoryFilter, machineId, page },
    ],
    queryFn: () =>
      sopsAPI
        .getAll({
          search: search || undefined,
          status: statusFilter || undefined,
          category: categoryFilter || undefined,
          machineId: machineId || undefined,
          page,
          limit: PAGE_SIZE,
        })
        .then((res) => res.data),
  });

  const { data: categories } = useQuery({
    queryKey: ["sop-categories"],
    queryFn: () => sopsAPI.getCategories().then((res) => res.data.data),
  });

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusFilterRef.current &&
        !statusFilterRef.current.contains(event.target)
      ) {
        setIsStatusFilterOpen(false);
      }
      if (
        categoryFilterRef.current &&
        !categoryFilterRef.current.contains(event.target)
      ) {
        setIsCategoryFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const rawSOPs = sopsData?.data || [];
  const pagination = sopsData?.pagination || {};
  const totalItems = pagination.total !== undefined ? pagination.total : rawSOPs.length;
  const totalPages = pagination.pages || Math.ceil(totalItems / PAGE_SIZE) || 1;

  // Ensure maximum 10 items displayed per page
  const displayedSOPs =
    rawSOPs.length > PAGE_SIZE
      ? rawSOPs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
      : rawSOPs;

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(1);
    }
  }, [totalPages, page]);

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (page <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }
    if (page >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [
      1,
      "...",
      page - 1,
      page,
      page + 1,
      "...",
      totalPages,
    ];
  };

  return (
    <div className="space-y-5 lg:space-y-6">
      {/* Top Header matching Dashboard & Machines */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
            Standard Operating Procedures
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Standardized operational guidelines, maintenance workflows & plant protocols
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
            <Link
              to="/sops/create"
              className="btn btn-primary text-xs sm:text-sm py-2 px-3.5 sm:px-4 shadow-md shadow-blue-500/20 whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              <span>Create SOP</span>
            </Link>
          </HasPermission>
        </div>
      </div>

      {/* Machine Filter Notice if filtered by query param */}
      {machineId && (
        <div className="flex items-center justify-between p-3 sm:p-3.5 rounded-2xl bg-blue-50/80 border border-blue-200/80 text-blue-900 text-xs">
          <div className="flex items-center gap-2.5 min-w-0">
            <Cpu className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <span className="truncate">
              Filtering procedures for machine:{" "}
              <strong className="font-semibold text-blue-950">
                {displayedSOPs[0]?.machine_name || machineId}
              </strong>
            </span>
          </div>
          <Link
            to="/sops"
            className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 flex-shrink-0 ml-2"
          >
            <span>Clear filter</span>
            <X className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* Search & Control Filter Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Search Input */}
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search procedures by title or keyword..."
            className="w-full pl-9 pr-9 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-xs transition-all"
          />
          {search && (
            <button
              onClick={() => {
                setSearch("");
                setPage(1);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filter Dropdowns */}
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
          {/* Status Filter Dropdown */}
          <div ref={statusFilterRef} className="relative w-full sm:w-auto">
            <button
              onClick={() => setIsStatusFilterOpen((prev) => !prev)}
              className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-xs hover:border-slate-400 rounded-xl"
            >
              <div className="flex items-center gap-2">
                <Filter className="w-3.5 h-3.5 text-slate-500" />
                {statusFilter && statusConfig[statusFilter] ? (
                  <div className="flex items-center gap-1.5">
                    <span
                      className={clsx(
                        "w-2 h-2 rounded-full",
                        statusConfig[statusFilter].dot
                      )}
                    />
                    <span className="font-semibold text-slate-700">
                      {statusConfig[statusFilter].label}
                    </span>
                  </div>
                ) : (
                  <span className="font-semibold text-slate-700">All Status</span>
                )}
              </div>
              <ChevronDown
                className={clsx(
                  "w-3.5 h-3.5 text-slate-500 transition-transform duration-200",
                  isStatusFilterOpen && "rotate-180"
                )}
              />
            </button>

            <AnimatePresence>
              {isStatusFilterOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-1.5 w-full sm:w-52 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200/90 py-1.5 z-20"
                >
                  <button
                    onClick={() => {
                      setStatusFilter("");
                      setPage(1);
                      setIsStatusFilterOpen(false);
                    }}
                    className={clsx(
                      "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                      statusFilter === ""
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    <span>All Status</span>
                    {statusFilter === "" && (
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    )}
                  </button>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setStatusFilter(key);
                        setPage(1);
                        setIsStatusFilterOpen(false);
                      }}
                      className={clsx(
                        "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                        statusFilter === key
                          ? "bg-blue-50 text-blue-700 font-semibold"
                          : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className={clsx("w-2 h-2 rounded-full", config.dot)} />
                        <span>{config.label}</span>
                      </div>
                      {statusFilter === key && (
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                      )}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Category Filter Dropdown */}
          <div ref={categoryFilterRef} className="relative w-full sm:w-auto">
            <button
              onClick={() => setIsCategoryFilterOpen((prev) => !prev)}
              className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-xs hover:border-slate-400 rounded-xl"
            >
              <div className="flex items-center gap-2 truncate max-w-[140px] sm:max-w-none">
                <span className="font-semibold text-slate-700 truncate">
                  {categoryFilter || "All Categories"}
                </span>
              </div>
              <ChevronDown
                className={clsx(
                  "w-3.5 h-3.5 text-slate-500 transition-transform duration-200",
                  isCategoryFilterOpen && "rotate-180"
                )}
              />
            </button>

            <AnimatePresence>
              {isCategoryFilterOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-1.5 w-full sm:w-56 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200/90 py-1.5 z-20 max-h-64 overflow-y-auto"
                >
                  <button
                    onClick={() => {
                      setCategoryFilter("");
                      setPage(1);
                      setIsCategoryFilterOpen(false);
                    }}
                    className={clsx(
                      "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                      categoryFilter === ""
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    <span>All Categories</span>
                    {categoryFilter === "" && (
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    )}
                  </button>
                  {categories?.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => {
                        setCategoryFilter(cat);
                        setPage(1);
                        setIsCategoryFilterOpen(false);
                      }}
                      className={clsx(
                        "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                        categoryFilter === cat
                          ? "bg-blue-50 text-blue-700 font-semibold"
                          : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <span className="truncate">{cat}</span>
                      {categoryFilter === cat && (
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Reset Filters Shortcut */}
          {(search || statusFilter || categoryFilter) && (
            <button
              onClick={() => {
                setSearch("");
                setStatusFilter("");
                setCategoryFilter("");
                setPage(1);
              }}
              className="text-xs font-semibold text-slate-500 hover:text-rose-600 px-2.5 py-1.5 rounded-xl hover:bg-rose-50 transition-colors"
              title="Reset all filters"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* SOPs Listing View (Maintained Listing Format) */}
      {isLoading ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="divide-y divide-slate-100">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="grid grid-cols-12 items-center p-4 border-b border-slate-100 last:border-b-0 animate-pulse"
              >
                {/* Title/Category (4) */}
                <div className="col-span-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-slate-200 flex-shrink-0" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-4 w-44 bg-slate-200 rounded" />
                    <div className="h-3 w-24 bg-slate-100 rounded" />
                  </div>
                </div>
                {/* Status (2) */}
                <div className="col-span-2">
                  <div className="h-6 w-24 bg-slate-200 rounded-full" />
                </div>
                {/* Skill Level (2) */}
                <div className="col-span-2">
                  <div className="h-5 w-20 bg-slate-100 rounded-lg" />
                </div>
                {/* Duration (2) */}
                <div className="col-span-2">
                  <div className="h-4 w-16 bg-slate-100 rounded" />
                </div>
                {/* Steps (2) */}
                <div className="col-span-2">
                  <div className="h-5 w-12 bg-slate-100 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : displayedSOPs && displayedSOPs.length > 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              {/* Table Header (4-2-2-2-2 = 12 cols) */}
              <div className="grid grid-cols-12 items-center text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50/80 border-b border-slate-200/80 px-4 py-3">
                <div className="col-span-4">SOP / Category</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Skill Level</div>
                <div className="col-span-2">Duration</div>
                <div className="col-span-2">Steps</div>
              </div>

              {/* List Body */}
              <div className="divide-y divide-slate-100/90">
                <AnimatePresence>
                  {displayedSOPs.map((sop, index) => (
                    <SOPListItem
                      key={sop.id}
                      sop={sop}
                      index={index}
                      onDelete={setDeletingSop}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-10 sm:p-14 text-center">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-500 mx-auto mb-4 flex items-center justify-center">
            <FileText className="w-8 h-8" />
          </div>
          <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1.5">
            No SOPs Found
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 max-w-sm mx-auto mb-5">
            {search || statusFilter || categoryFilter || machineId
              ? "No standard operating procedures match your current search and filter criteria."
              : "No standard operating procedures created yet. Get started by drafting your first SOP."}
          </p>
          <div className="flex items-center justify-center gap-3">
            {(search || statusFilter || categoryFilter || machineId) && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("");
                  setCategoryFilter("");
                  setPage(1);
                }}
                className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 rounded-xl"
              >
                Clear Filters
              </button>
            )}
            <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
              <Link
                to="/sops/create"
                className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 inline-flex items-center gap-2 rounded-xl"
              >
                <Plus className="w-4 h-4" />
                <span>Create SOP</span>
              </Link>
            </HasPermission>
          </div>
        </div>
      )}

      {/* Pagination Controls - only shown after 10 items (totalItems > PAGE_SIZE) */}
      {!isLoading && totalItems > PAGE_SIZE && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200/80">
          <p className="text-xs text-slate-500 font-medium">
            Showing{" "}
            <span className="font-semibold text-slate-700">
              {(page - 1) * PAGE_SIZE + 1}
            </span>{" "}
            to{" "}
            <span className="font-semibold text-slate-700">
              {Math.min(page * PAGE_SIZE, totalItems)}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-slate-700">{totalItems}</span>{" "}
            procedures
          </p>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              className={clsx(
                "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                page <= 1
                  ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                  : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
              )}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Prev</span>
            </button>

            <div className="flex items-center gap-1">
              {getPageNumbers().map((pageItem, idx) =>
                pageItem === "..." ? (
                  <span
                    key={`ellipsis-${idx}`}
                    className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={pageItem}
                    type="button"
                    onClick={() => setPage(pageItem)}
                    className={clsx(
                      "w-8 h-8 rounded-xl text-xs font-semibold transition-all",
                      page === pageItem
                        ? "bg-blue-600 text-white shadow-sm shadow-blue-500/30 font-bold"
                        : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 shadow-xs"
                    )}
                  >
                    {pageItem}
                  </button>
                )
              )}
            </div>

            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              className={clsx(
                "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                page >= totalPages
                  ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                  : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
              )}
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {deletingSop && (
              <div
                className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
                onClick={() => {
                  if (!deleteMutation.isPending) setDeletingSop(null);
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="relative w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 space-y-4 my-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 border border-rose-100/80 flex items-center justify-center shrink-0">
                      <Trash2 className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                        Delete Procedure
                      </h2>
                    </div>
                  </div>

                  {/* Warning Description */}
                  <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
                    Are you sure you want to delete{" "}
                    <span className="font-semibold text-slate-800">
                      {deletingSop.title}
                    </span>
                    ? This action cannot be undone. Any operational workflows referencing this SOP will be unlinked.
                  </p>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      disabled={deleteMutation.isPending}
                      onClick={() => setDeletingSop(null)}
                      className="btn btn-secondary text-xs sm:text-sm py-2 px-4 hover:border-slate-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={deleteMutation.isPending}
                      onClick={handleDeleteConfirm}
                      className="btn bg-rose-600 hover:bg-rose-700 text-white text-xs sm:text-sm py-2 px-4 shadow-md shadow-rose-500/20 inline-flex items-center gap-2 font-medium"
                    >
                      {deleteMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          <span>Delete Procedure</span>
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default SOPs;
